/**
 * What a failed thread fetch means for a reader (#803).
 *
 * `apiFetch` throws the axios error (lib/api.js), so the status and the
 * backend's `detail` are on `.response` — FastAPI's default `{"detail": …}`
 * shape; the server registers no HTTPException handler of its own.
 *
 * Keyed on the DETAIL, not the status alone: every /api/inboxes/* route
 * answers 404 "not enabled" while the unified-inbox flag is off
 * (services/inbox_feature.py; `isFeatureOff` in useUnifiedInbox.js relies on
 * it), and that is not "this email was deleted".
 */

/** The thread endpoints' "no such thread" — routes/personal_inbox.py,
 *  routes/microsoft_inbox.py `get_inbox_thread`, and the shared inbox's Trash
 *  view (`GET /inboxes/{id}/trash/{seed}`: restored or deleted for good). */
const GONE = new Set(['Thread not found', 'thread_not_found', 'message_not_in_trash']);

/**
 * `'gone'`          — the thread no longer exists for this reader
 * `'inbox_missing'` — the shared inbox itself was deleted
 * `'forbidden'`     — the reader lost their grant on the inbox
 * `'failed'`        — anything else (5xx, network, the feature-flag 404):
 *                     transient or not ours to explain, so retryable
 * `null`            — no error
 */
export function classifyThreadError(error) {
    if (!error) return null;
    const status = error.response?.status;
    const detail = error.response?.data?.detail;
    if (status === 404 && GONE.has(detail)) return 'gone';
    if (status === 404 && detail === 'inbox_not_found') return 'inbox_missing';
    if (status === 403 && detail === 'inbox_access_denied') return 'forbidden';
    return 'failed';
}

/** States in which a reader must neither show nor act on any thread data it
 *  still holds — React Query keeps the last good `data` after a failed refetch. */
export const TERMINAL_THREAD_ERRORS = new Set(['gone', 'inbox_missing', 'forbidden']);
